import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Activity, ChevronRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn, getDefensiveSignal } from '../lib/utils';
import { PITCH_COUNT_RULESETS, getPitcherEligibility, buildCustomTournamentRules } from '../lib/pitch-count-rules';
import { getPitchingIntel } from '../lib/pitching-intel';
import { isSameDay } from 'date-fns';

// One labeled progress line for a single scope (Day / Tournament). Color escalates
// with usage so a pitcher nearing a cap reads red at a glance.
const LimitBar = ({ scope, used, max, remaining }: { scope: string; used: number; max: number; remaining: number }) => {
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const color = pct >= 100 || remaining <= 0 ? 'bg-red-500'
    : pct > 80 ? 'bg-red-500'
    : pct > 50 ? 'bg-gold'
    : 'bg-ink';
  const remainingColor = remaining <= 0 ? 'text-danger' : 'text-ink-2';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] font-semibold uppercase tracking-widest">
        <span className="text-ink-2">{scope}</span>
        <span className={remainingColor}>{used}/{max} · {Math.max(0, remaining)} left</span>
      </div>
      <div className="h-2 bg-surface rounded-full overflow-hidden border border-hairline">
        <div className={cn('h-full transition-all', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

interface PitchingIntelPanelProps {
  lineup: any[];
  pitchCounts: Record<string, number>;
  pitchedThisGame: string[];
  inningLogs: Record<number, { id: string; position: string }[]>;
  team: any;
  players: any[];
  teamId: string;
  gameId: string;
  games?: any[];
  tournamentId?: string;
  /** The resolved tournament doc, when this game belongs to one. Lets custom
      pitch rules defined per-tournament override the season-level defaults. */
  tournament?: any;
  gameDate?: string | Date;
  enforceGlobalRest?: boolean;
  rulesetOverride?: string;
  onSubstitution: (newLineup: any[], inningPitches?: number) => void;
  onClose: () => void;
}

export const PitchingIntelPanel = ({
  lineup,
  pitchCounts,
  pitchedThisGame,
  inningLogs,
  team,
  players,
  games = [],
  tournamentId,
  tournament,
  gameDate,
  enforceGlobalRest = true,
  rulesetOverride,
  onSubstitution,
  onClose
}: PitchingIntelPanelProps) => {
  const settings = team.settings || {};

  const rulesetKey = rulesetOverride
    || (tournamentId ? settings.tournamentOrg : null)
    || settings.pitchCountRuleset
    || 'little_league';
  const ruleset = PITCH_COUNT_RULESETS[rulesetKey] || PITCH_COUNT_RULESETS.little_league;
  const effectiveCustomRules = buildCustomTournamentRules(
    !!tournamentId && ruleset.id === 'custom',
    tournament,
    settings,
  );
  const ageGroup = settings.pitchCountAgeGroup || team.ageGroup || '12U';
  const today = gameDate ? new Date(gameDate) : new Date();

  // Calculate cumulative daily pitch counts for tournament
  const dailyPitchCounts: Record<string, number> = {};
  if (tournamentId && games.length > 0) {
    games.forEach(g => {
      if (g.tournamentId === tournamentId && isSameDay(new Date(g.date), today) && g.pitchCounts) {
        Object.entries(g.pitchCounts).forEach(([pid, count]) => {
          dailyPitchCounts[pid] = (dailyPitchCounts[pid] || 0) + (count as number);
        });
      }
    });
  }
  
  const currentPitcher = lineup.find(p => p.position === 'P');
  const currentPitchCount = currentPitcher ? (pitchCounts[currentPitcher.id] || 0) : 0;
  const currentDailyCount = currentPitcher ? (dailyPitchCounts[currentPitcher.id] || 0) : 0;

  const currentIntel = currentPitcher ? getPitchingIntel(
    currentPitcher.pitchingLog || [],
    ruleset,
    ageGroup,
    today,
    currentDailyCount + currentPitchCount,
    tournamentId,
    enforceGlobalRest,
    effectiveCustomRules
  ) : null;

  const PITCH_LIMIT = currentIntel?.dailyMax || 85;

  const getPitchingOVR = (player: any) => {
    if (!player?.attributes?.pitching) return 0;
    return parseFloat((player.attributes.pitching.velocity * 0.6 + player.attributes.pitching.control * 0.4).toFixed(1));
  };

  const getPositionRating = (player: any, position: string) => {
    return player.positions?.[position] || 0;
  };

  // Full pitching intel for any player, using the same daily+current-game count
  // basis as the warnings calc. Drives the per-arm Day/Tournament limit lines.
  const getPlayerIntel = (player: any) => getPitchingIntel(
    player.pitchingLog || [],
    ruleset,
    ageGroup,
    today,
    (dailyPitchCounts[player.id] || 0) + (pitchCounts[player.id] || 0),
    tournamentId,
    enforceGlobalRest,
    effectiveCustomRules,
  );

  // Compute all warnings for a pitcher: ruleset violations + custom setting overrides.
  // Returns an array of human-readable warning strings. Empty = no warnings.
  const getPitcherWarnings = (player: any): string[] => {
    const warnings: string[] = [];
    const dailyCount = dailyPitchCounts[player.id] || 0;
    const intel = getPitchingIntel(
      player.pitchingLog || [],
      ruleset,
      ageGroup,
      today,
      dailyCount + (pitchCounts[player.id] || 0),
      tournamentId,
      enforceGlobalRest,
      effectiveCustomRules
    );
    if (!intel.isEligible && intel.reason) warnings.push(intel.reason);
    if ((intel as any).warningReason) warnings.push((intel as any).warningReason);

    const todayStr = today.toISOString().split('T')[0];

    // Innings pitched this game from inningLogs
    const inningsThisGame = Object.values(inningLogs)
      .filter(entries => (entries as any[]).some((e: any) => e.id === player.id && e.position === 'P'))
      .length;

    if (settings.maxInningsPerGameEnabled && (settings.maxInningsPerGame || 0) > 0 && inningsThisGame >= settings.maxInningsPerGame) {
      warnings.push(`At ${settings.maxInningsPerGame}-inning game limit`);
    }

    if (settings.maxAppearancesPer3DaysEnabled && (settings.maxAppearancesPer3Days || 0) > 0) {
      const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 2);
      const cutoffStr = cutoff.toISOString().split('T')[0];
      const appearances = (player.pitchingLog || [])
        .filter((e: any) => e.date.split('T')[0] >= cutoffStr && e.pitchCount > 0).length;
      if (appearances >= settings.maxAppearancesPer3Days) {
        warnings.push(`${appearances} appearances in last 3 days (limit ${settings.maxAppearancesPer3Days})`);
      }
    }

    if (settings.noConsecutiveDaysPitching) {
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
      const yStr = yesterday.toISOString().split('T')[0];
      const pitchedYesterday = (player.pitchingLog || []).some((e: any) => e.date.split('T')[0] === yStr && e.pitchCount > 0);
      if (pitchedYesterday) warnings.push('Pitched yesterday, no consecutive days');
    }

    if (settings.maxInningsPerDayEnabled && (settings.maxInningsPerDay || 0) > 0) {
      const inningsToday = (player.pitchingLog || [])
        .filter((e: any) => e.date.split('T')[0] === todayStr)
        .reduce((s: number, e: any) => s + (e.inningsPitched || 0), 0);
      if (inningsToday + inningsThisGame >= settings.maxInningsPerDay) {
        warnings.push(`At ${settings.maxInningsPerDay}-inning daily limit`);
      }
    }

    if (settings.maxInningsPerWeekEnabled && (settings.maxInningsPerWeek || 0) > 0) {
      const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 6);
      const cutoffStr = cutoff.toISOString().split('T')[0];
      const inningsThisWeek = (player.pitchingLog || [])
        .filter((e: any) => e.date.split('T')[0] >= cutoffStr)
        .reduce((s: number, e: any) => s + (e.inningsPitched || 0), 0);
      if (inningsThisWeek + inningsThisGame >= settings.maxInningsPerWeek) {
        warnings.push(`At ${settings.maxInningsPerWeek}-inning weekly limit`);
      }
    }

    return warnings;
  };

  const bullpenPlayers = lineup.filter(p => {
    const isPitcher = p.positions?.['P'] >= 1 ||
      (p.attributes?.pitching?.velocity >= 1 || p.attributes?.pitching?.control >= 1);

    if (!isPitcher || p.position === 'P') return false;
    if (settings.excludeCatcherFromBullpen && p.position === 'C') return false;

    // Exclude anyone who has already pitched this game — no backdoor re-entry.
    if (pitchedThisGame.includes(p.id)) return false;

    return true;
  }).sort((a, b) => getPitchingOVR(b) - getPitchingOVR(a));

  // Cascade Calculator Logic
  const calculateSubImpact = (reliever: any) => {
    const changes: { pos: string; from: string; to: string; impact: number }[] = [];
    let currentLineupState = [...lineup.map(p => ({ ...p }))];
    
    // 1. Reliever moves to P
    const oldPos = reliever.position;
    const relieverInLineup = currentLineupState.find(p => p.id === reliever.id);
    if (relieverInLineup) relieverInLineup.position = 'P';
    
    // If there was a pitcher, they move to BENCH (default)
    const outgoingPitcher = currentLineupState.find(p => p.id !== reliever.id && p.position === 'P');
    if (outgoingPitcher) outgoingPitcher.position = 'BENCH';

    changes.push({
      pos: 'P',
      from: currentPitcher?.name || 'None',
      to: reliever.name || 'Unknown',
      impact: 0 // Pitching impact is handled separately in UI
    });

    // 2. Fill the vacancy left by reliever
    let vacantPos = oldPos;
    
    // Cascade loop
    while (vacantPos !== 'BENCH' && vacantPos !== 'EH') {
      // Find best replacement from bench/EH
      const candidates = currentLineupState
        .filter(p => p.position === 'BENCH' || p.position === 'EH')
        .sort((a, b) => getPositionRating(b, vacantPos) - getPositionRating(a, vacantPos));

      const bestBench = candidates[0];
      
      if (bestBench) {
        const oldRating = 0; // Vacant
        const newRating = getPositionRating(bestBench, vacantPos);
        
        changes.push({
          pos: vacantPos,
          from: 'Vacant',
          to: bestBench.name || 'Unknown',
          impact: newRating - oldRating
        });
        
        bestBench.position = vacantPos;
        vacantPos = 'BENCH'; // Filled from bench, cascade stops
      } else {
        // No bench players? This shouldn't happen in a standard game but handle it
        break;
      }
    }

    const netImpact = changes.reduce((acc, curr) => acc + curr.impact, 0);

    return {
      newLineup: currentLineupState,
      changes,
      netImpact,
      intel: getPitchingIntel(
        reliever.pitchingLog || [],
        ruleset,
        ageGroup,
        today,
        (dailyPitchCounts[reliever.id] || 0) + (pitchCounts[reliever.id] || 0),
        tournamentId,
        enforceGlobalRest,
        effectiveCustomRules
      )
    };
  };

  const subOptions = bullpenPlayers.map(p => ({
    player: p,
    ...calculateSubImpact(p)
  })).sort((a, b) => {
    const ovrDiff = getPitchingOVR(b.player) - getPitchingOVR(a.player);
    if (Math.abs(ovrDiff) > 0.5) return ovrDiff;
    return b.netImpact - a.netImpact;
  });

  // ── Confirmation + pitch-log flow ─────────────────────────────────────────
  type PendingOption = typeof subOptions[0];
  const [pendingOption, setPendingOption] = useState<PendingOption | null>(null);
  const [pitchStep, setPitchStep] = useState<'confirm' | 'log_pitches' | null>(null);
  const [pitchesInput, setPitchesInput] = useState('');

  const handleOptionClick = (option: PendingOption) => {
    setPendingOption(option);
    setPitchStep('confirm');
    setPitchesInput('');
  };

  const handleConfirm = () => setPitchStep('log_pitches');

  const handleCancel = () => {
    setPendingOption(null);
    setPitchStep(null);
    setPitchesInput('');
  };

  const commitSub = (pitches?: number) => {
    if (!pendingOption) return;
    onSubstitution(pendingOption.newLineup, pitches);
    setPendingOption(null);
    setPitchStep(null);
    setPitchesInput('');
  };

  return (
    <>
      {/* Dim backdrop — tap to dismiss. Makes this read as a sheet, not a
          full-screen takeover. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[59] bg-surface/30"
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        // Bottom sheet: anchored to the bottom, rises only as tall as its content
        // up to a cap (so a short panel doesn't fill the screen). Never reaches the
        // top, so the header can't collide with the Dynamic Island.
        className="fixed bottom-0 left-0 right-0 z-[60] bg-surface rounded-t-3xl flex flex-col max-h-[88vh] shadow-2xl"
      >
      {/* Grabber handle — the visual cue for a draggable/dismissable sheet. */}
      <div className="flex justify-center pt-3 pb-1 shrink-0">
        <div className="w-10 h-1.5 rounded-full bg-hairline" />
      </div>
      <header className="px-6 py-4 border-b border-hairline flex items-center justify-between bg-surface shrink-0">
        <div>
          <h2 className="text-xl font-black italic uppercase flex items-center space-x-2">
            <Activity className="w-6 h-6 text-accent-ink" />
            <span>Pitching Intelligence</span>
          </h2>
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Deterministic Tactical Analysis</p>
        </div>
        <button onClick={onClose} className="p-2 bg-canvas rounded-lg text-ink-2 hover:text-ink">
          <X className="w-5 h-5" />
        </button>
      </header>

      <div className="overflow-y-auto p-6 space-y-8 safe-bottom">
        {/* Section 1: Pitch Check */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitch Check</h3>
          
          {/* Current Pitcher Card */}
          {currentPitcher && (
            <div className="bg-canvas rounded-3xl p-6 border border-hairline">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest mb-1">Current Pitcher</div>
                  <h4 className="text-lg font-black uppercase">{currentPitcher.name || 'Unknown'} <span className="text-ink-2">#{currentPitcher.number}</span></h4>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black">{currentPitchCount}</div>
                  <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitches</div>
                </div>
              </div>

                  {/* Two-line limit readout. In a tournament with an event cap we
                      show BOTH the per-day and per-tournament limits explicitly so
                      the coach sees, at a glance, how many a pitcher has left in
                      each scope. Outside a tournament we keep the single daily bar. */}
                  {tournamentId && currentIntel?.eventMax != null ? (
                    <div className="space-y-3">
                      <LimitBar
                        scope="Day"
                        used={currentIntel.dailyUsed}
                        max={currentIntel.dailyMax}
                        remaining={currentIntel.dailyRemaining}
                      />
                      <LimitBar
                        scope="Tournament"
                        used={currentIntel.eventUsed ?? 0}
                        max={currentIntel.eventMax}
                        remaining={currentIntel.eventRemaining ?? 0}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-3 bg-surface rounded-full overflow-hidden border border-hairline">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(currentIntel?.percent || 0, 100)}%` }}
                          className={cn(
                            "h-full transition-colors",
                            currentIntel?.color === 'red' ? "bg-red-500" :
                            currentIntel?.color === 'amber' ? "bg-gold" : "bg-ink"
                          )}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] font-semibold uppercase tracking-widest">
                        <span className="text-ink-2">0</span>
                        <span className={cn(
                          currentIntel?.color === 'red' ? "text-danger" : "text-ink-2"
                        )}>
                          {currentIntel?.label}
                        </span>
                        <span className="text-ink-2">{PITCH_LIMIT}</span>
                      </div>
                    </div>
                  )}
            </div>
          )}

          {/* Available Arms List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bullpenPlayers.map(player => {
              const warnings = getPitcherWarnings(player);
              const hasWarning = warnings.length > 0;
              const intel = getPlayerIntel(player);
              const showLimits = tournamentId && intel.eventMax != null;
              return (
                <div key={player.id} className={cn(
                  "rounded-2xl p-4 border flex flex-col gap-2",
                  hasWarning ? "bg-red-950/20 border-hairline" : "bg-canvas border-hairline"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-raised rounded-xl flex items-center justify-center font-black text-ink-2 relative">
                        #{player.number}
                        <div className={cn("absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-hairline", getDefensiveSignal(player, 'P'))} />
                      </div>
                      <div>
                        <div className="font-black uppercase text-sm">{player.name || 'Unknown'}</div>
                        <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Currently {player.position}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={cn("text-sm font-black", hasWarning ? "text-danger" : "text-accent-ink")}>{getPitchingOVR(player)} OVR</div>
                      {!showLimits && (
                        <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                          {tournamentId ? `${(dailyPitchCounts[player.id] || 0) + (pitchCounts[player.id] || 0)} Today` : `${pitchCounts[player.id] || 0} Pitches`}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Per-arm two-line limit readout: how many pitches this player
                      has left today and this tournament, at a glance. */}
                  {showLimits && (
                    <div className="space-y-2">
                      <LimitBar scope="Day" used={intel.dailyUsed} max={intel.dailyMax} remaining={intel.dailyRemaining} />
                      <LimitBar scope="Tournament" used={intel.eventUsed ?? 0} max={intel.eventMax!} remaining={intel.eventRemaining ?? 0} />
                    </div>
                  )}
                  {hasWarning && (
                    <div className="space-y-1">
                      {warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="text-danger text-[9px] mt-0.5">⚠</span>
                          <span className="text-[9px] font-semibold text-danger uppercase tracking-wide leading-tight">{w}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 2: Sub Impact */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Sub Impact (Cascade Calculator)</h3>
          
          <div className="space-y-4">
            {subOptions.map((option, idx) => {
              const warnings = getPitcherWarnings(option.player);
              const hasWarning = warnings.length > 0;
              return (
                <button
                  key={`${option.player.id}-${idx}`}
                  onClick={() => handleOptionClick(option)}
                  className={cn(
                    "w-full rounded-3xl p-6 border transition-all text-left group",
                    hasWarning
                      ? "bg-red-950/20 border-hairline hover:border-red-500/60"
                      : "bg-canvas border-hairline hover:border-ink-mute"
                  )}
                >
                  {hasWarning && (
                    <div className="mb-4 px-3 py-2 bg-[#FBF0F0] rounded-xl border border-hairline space-y-1">
                      {warnings.map((w, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <span className="text-danger text-[9px]">⚠</span>
                          <span className="text-[9px] font-semibold text-danger uppercase tracking-wide">{w}</span>
                        </div>
                      ))}
                      <p className="text-[8px] text-danger/60 font-semibold uppercase tracking-wider mt-1">Coach override: tap to assign anyway</p>
                    </div>
                  )}
                  <div className="flex justify-between items-start gap-3 mb-6">
                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                      <div className={cn(
                        "w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl transition-colors shrink-0",
                        hasWarning ? "bg-red-950/40 text-danger" : "bg-surface text-ink-2 group-hover:text-accent-ink"
                      )}>
                        #{option.player.number}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-lg font-black uppercase flex items-center space-x-2 min-w-0">
                          <span className="truncate">{option.player.name || 'Unknown'}</span>
                          <ChevronRight className="w-4 h-4 text-ink-2 shrink-0" />
                          <span className="text-accent-ink shrink-0">Pitcher</span>
                        </h4>
                        <div className="flex items-center space-x-4 mt-1">
                          <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitching OVR: <span className="text-ink">{getPitchingOVR(option.player)}</span></div>
                          <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Status: <span className={cn(
                            option.intel.color === 'red' ? "text-danger" :
                            option.intel.color === 'amber' ? "text-gold-ink" : "text-accent-ink"
                          )}>{option.intel.label}</span></div>
                        </div>
                      </div>
                    </div>
                    <div className={cn(
                      "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 shrink-0 whitespace-nowrap",
                      option.netImpact > 0 ? "bg-canvas text-accent-ink" :
                      option.netImpact < 0 ? "bg-[#FBF0F0] text-danger" : "bg-raised text-ink-2"
                    )}>
                      {option.netImpact > 0 ? <TrendingUp className="w-3 h-3 shrink-0" /> :
                       option.netImpact < 0 ? <TrendingDown className="w-3 h-3 shrink-0" /> : <Minus className="w-3 h-3 shrink-0" />}
                      <span>Net Impact: {option.netImpact > 0 ? '+' : ''}{option.netImpact}</span>
                    </div>
                  </div>

                  <div className="space-y-3 bg-surface rounded-2xl p-4 border border-hairline">
                    <div className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-2">Defensive Cascade</div>
                    {option.changes.map((change, cIdx) => (
                      <div key={cIdx} className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2">
                          <span className="font-black text-ink-2 w-6">{change.pos}</span>
                          <span className="text-ink-2">{change.from}</span>
                          <ChevronRight className="w-3 h-3 text-ink-2" />
                          <span className="font-bold text-ink">{change.to}</span>
                        </div>
                        {change.impact !== 0 && (
                          <span className={cn(
                            "font-bold",
                            change.impact > 0 ? "text-accent-ink" : "text-danger"
                          )}>
                            {change.impact > 0 ? '+' : ''}{change.impact}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}

            {subOptions.length === 0 && (
              <div className="bg-canvas rounded-3xl p-12 border border-hairline border-dashed text-center">
                <p className="text-ink-2 font-bold uppercase tracking-widest text-sm">No available relief arms</p>
                <p className="text-ink-2 text-[10px] font-semibold uppercase tracking-widest mt-1">All pitchers have already thrown today</p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Confirmation + pitch-log overlay */}
      <AnimatePresence>
        {pitchStep && pendingOption && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-end justify-center p-4"
          >
            <motion.div
              initial={{ y: 40, scale: 0.96 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 40, scale: 0.96 }}
              className="w-full max-w-md bg-canvas border border-ink-mute rounded-3xl overflow-hidden shadow-2xl"
            >
              {pitchStep === 'confirm' ? (
                <div className="p-6 space-y-6">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-accent-ink mb-1">Pitching Change</p>
                    <h3 className="text-lg font-black uppercase leading-tight">
                      Bring in {pendingOption.player.name}?
                    </h3>
                    <p className="text-sm text-ink-2 mt-1">
                      {currentPitcher?.name} will be removed from the mound.
                    </p>
                  </div>

                  <div className="bg-surface rounded-2xl p-4 border border-hairline space-y-2">
                    <p className="text-[9px] font-semibold uppercase tracking-widest text-ink-2 mb-2">Defensive Cascade</p>
                    {pendingOption.changes.map((change, i) => (
                      <div key={i} className="flex items-center space-x-2 text-xs">
                        <span className="font-black text-ink-2 w-6">{change.pos}</span>
                        <span className="text-ink-2">{change.from}</span>
                        <ChevronRight className="w-3 h-3 text-ink-2" />
                        <span className="font-bold text-ink">{change.to}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleCancel}
                      className="py-3 bg-raised text-ink-2 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-hairline transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="py-3 bg-ink text-surface rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-ink transition-colors"
                    >
                      Confirm Change
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gold-ink mb-1">Log Pitches</p>
                    <h3 className="text-lg font-black uppercase leading-tight">
                      {currentPitcher?.name || 'Outgoing Pitcher'}
                    </h3>
                    <p className="text-sm text-ink-2 mt-1">
                      How many pitches this inning before the change?
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-2">
                      Total so far: {currentPitchCount} pitches
                    </p>
                    <input
                      type="number"
                      value={pitchesInput}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        if (!val) { setPitchesInput(''); return; }
                        setPitchesInput(String(Math.min(parseInt(val, 10), 200)));
                      }}
                      placeholder="0"
                      autoFocus
                      className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-ink font-black text-2xl outline-none focus:border-ink transition-colors text-center"
                    />
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => commitSub(parseInt(pitchesInput, 10) || 0)}
                      disabled={!pitchesInput}
                      className="w-full py-3 bg-ink text-surface rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-ink transition-colors disabled:opacity-40"
                    >
                      Log & Make Change
                    </button>
                    <button
                      onClick={() => commitSub(undefined)}
                      className="w-full py-3 bg-raised text-ink-2 rounded-2xl text-xs font-black uppercase tracking-widest hover:text-ink transition-colors"
                    >
                      Skip, Enter Later
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </motion.div>
    </>
  );
};
